/**
 * Warmer or colder.
 *
 * Under thirty metres the product already admits the bearing is untrustworthy, because relative
 * GPS error is comparable to the gap. What it did not say is that the *trend* survives perfectly
 * well: both fixes wander, but they wander slowly and roughly together, so "the gap has shrunk
 * by four metres in the last ten seconds" stays true when "they are that way" has stopped being.
 *
 * That turns the weakest part of the product into a mechanic. Instead of a ring that pulses at
 * a distance nobody trusts, you get an answer to the only question that matters at that range:
 * is what I am doing working?
 *
 * The window is deliberately long. A three-second window measures GPS noise; a fifteen-second
 * one measures walking.
 */

/** How far back to look. Long enough that noise averages out and walking does not. */
const WINDOW_MS = 15_000;

/** Below this, the change is indistinguishable from two fixes jittering. */
const NOISE_FLOOR_M = 3;

/** Not enough history to say anything yet — and saying nothing is the honest answer. */
const MIN_SAMPLES = 3;

export type ClosingTrend = 'closing' | 'opening' | 'steady' | 'unknown';

export interface Closing {
  trend: ClosingTrend;
  /** Metres gained (positive) or lost (negative) over the window. Null when unknown. */
  metres: number | null;
  /** Seconds the window actually spans, which is less than `WINDOW_MS` early on. */
  seconds: number;
}

interface Sample {
  at: number;
  metres: number;
}

/**
 * Tracks the separation over a sliding window and says which way it is going.
 *
 * Fed the same distance the arrow is using, so the two can never disagree about how far away
 * anybody is.
 */
export class ClosingTracker {
  private readonly samples: Sample[] = [];

  /** Record a distance. Nulls are skipped rather than treated as zero. */
  record(distanceMetres: number | null, now: number): void {
    if (distanceMetres === null || !Number.isFinite(distanceMetres)) return;

    this.samples.push({ at: now, metres: distanceMetres });

    // Drop anything that has fallen out of the window. The array stays tiny — at one fix a
    // second this is fifteen entries.
    const cutoff = now - WINDOW_MS;
    while (this.samples.length > 0 && (this.samples[0] as Sample).at < cutoff) {
      this.samples.shift();
    }
  }

  /**
   * The trend right now.
   *
   * Compares the oldest sample in the window with the newest rather than fitting a line: over
   * fifteen seconds of walking the two agree, and the difference is a number a person can read
   * off the screen as "four metres closer" rather than a gradient.
   */
  read(): Closing {
    const first = this.samples[0];
    const last = this.samples[this.samples.length - 1];

    if (!first || !last || this.samples.length < MIN_SAMPLES) {
      return { trend: 'unknown', metres: null, seconds: 0 };
    }

    const seconds = Math.max(0, (last.at - first.at) / 1000);
    if (seconds < 2) return { trend: 'unknown', metres: null, seconds };

    // Positive means the gap shrank, which is the direction a person cares about.
    const gained = first.metres - last.metres;

    if (Math.abs(gained) < NOISE_FLOOR_M) return { trend: 'steady', metres: gained, seconds };
    return { trend: gained > 0 ? 'closing' : 'opening', metres: gained, seconds };
  }

  /** Start again — used when the arrow switches to a different person or place. */
  reset(): void {
    this.samples.length = 0;
  }
}

/**
 * The line the user reads.
 *
 * Deliberately concrete. "Getting warmer" is a hint; "12 m closer in the last 15 s" is a fact
 * somebody can act on, and the product's whole argument is that facts are what get trusted.
 */
export function describeClosing(closing: Closing): string | null {
  if (closing.trend === 'unknown' || closing.metres === null) return null;

  const seconds = Math.round(closing.seconds);
  const metres = Math.round(Math.abs(closing.metres));

  switch (closing.trend) {
    case 'closing':
      return `${metres} m closer in the last ${seconds} s`;
    case 'opening':
      return `${metres} m further away — try another direction`;
    case 'steady':
      return `No closer in the last ${seconds} s`;
  }
}
